import React, { memo, useEffect } from 'react';
import { Box, xcss } from '@atlaskit/primitives';
import { Emoji } from '@atlaskit/emoji/element';
import { ROUTE_NAMES_POLARIS_COLLECTIONS } from '@atlassian/jira-common-constants/src/spa-routes.tsx';
import { useOpenable } from '@atlassian/jira-navigation-apps-sidebar-nav4-sidebars-common-core/src/common/utils/use-openable/index.tsx';
import { getCollectionViewPageUrl } from '@atlassian/jira-polaris-component-collections/src/common/utils/collections.tsx';
import {
	useCollectionsActions,
	useCollectionName,
	useCollectionEmoji,
	useCollectionLoading,
	useCollectionViews,
} from '@atlassian/jira-polaris-component-collections/src/controllers/collections/index.tsx';
import { useCollectionEmojiIcon } from '@atlassian/jira-polaris-component-collections/src/ui/emoji/index.tsx';
import { ViewItemIcon } from '@atlassian/jira-polaris-component-glyphs/src/ui/glyphs/main.tsx';
import type { CollectionView } from '@atlassian/jira-polaris-domain-collection/src/view/index.tsx';
import type { ViewKind } from '@atlassian/jira-polaris-domain-view/src/view/types.tsx';
import { useEmoji } from '@atlassian/jira-polaris-lib-emoji-picker/src/controllers/index.tsx';
import {
	MenuList,
	MenuLinkItem,
	ExpandableMenuItem,
	ExpandableMenuItemContent,
	ExpandableMenuItemTrigger,
} from '@atlassian/navigation-system';
import { createRouterSelector } from '@atlassian/react-resource-router';

const useRouterParamsResource = createRouterSelector(({ match }) => match.params?.resource);

const useRouterParamsCollectionUUID = createRouterSelector(
	({ match }) => match.params?.collectionUUID,
);

const useIsCollectionSelected = createRouterSelector(({ route, match }) => {
	const collectionUUID = match.params?.resource;
	return route.name === ROUTE_NAMES_POLARIS_COLLECTIONS && collectionUUID !== undefined;
});

const ViewItems = ({
	collectionUUID,
	views,
}: {
	collectionUUID: string;
	views: CollectionView[];
}) => {
	const selectedViewUUID = useRouterParamsResource();

	return (
		<MenuList>
			{views.map((view) => (
				<MenuLinkItem
					key={view.uuid}
					href={getCollectionViewPageUrl(collectionUUID, view.uuid)}
					elemBefore={
						<ViewIcon
							emojiId={view.emoji}
							viewKind={view.kind}
							isSelected={view.uuid === selectedViewUUID}
						/>
					}
					isSelected={view.uuid === selectedViewUUID}
				>
					{view.title}
				</MenuLinkItem>
			))}
		</MenuList>
	);
};

// TODO create lib or component for all the things ViewIcon
type ViewIconProps = {
	emojiId?: string;
	viewKind: ViewKind;
	isSelected: boolean;
};

export const ViewIcon = ({ emojiId, viewKind }: ViewIconProps) => {
	const emoji = useEmoji(emojiId);
	if (emoji) {
		return (
			<Box xcss={emojiStyles}>
				<Emoji emoji={emoji} fitToHeight={18} showTooltip />
			</Box>
		);
	}

	return <ViewItemIcon viewKind={viewKind} />;
};

type ActiveRoadmapItemInternalProps = {
	collectionUUID: string;
};

const ActiveRoadmapItemInternal = memo(({ collectionUUID }: ActiveRoadmapItemInternalProps) => {
	const collectionName = useCollectionName({ collectionUUID });
	const collectionEmoji = useCollectionEmoji({ collectionUUID });
	const collectionViews = useCollectionViews({ collectionUUID });

	const EmojiIcon = useCollectionEmojiIcon(collectionEmoji);

	const { isOpen: isCollectionExpanded, onToggle: onExpansionToggle } = useOpenable(true);

	return (
		<ExpandableMenuItem isExpanded={isCollectionExpanded} onExpansionToggle={onExpansionToggle}>
			<ExpandableMenuItemTrigger elemBefore={<EmojiIcon label={collectionName || ''} />}>
				{collectionName}
			</ExpandableMenuItemTrigger>
			<ExpandableMenuItemContent>
				<ViewItems collectionUUID={collectionUUID} views={collectionViews} />
			</ExpandableMenuItemContent>
		</ExpandableMenuItem>
	);
});

export const ActiveRoadmapItem = () => {
	const isCollectionSelected = useIsCollectionSelected();
	const collectionUUID = useRouterParamsCollectionUUID() || '';

	const isLoading = useCollectionLoading({ collectionUUID });
	const { loadCollection } = useCollectionsActions();

	useEffect(() => {
		if (isCollectionSelected && collectionUUID) {
			loadCollection(collectionUUID, true);
		}
	}, [isCollectionSelected, collectionUUID, loadCollection]);

	if (!isCollectionSelected || !collectionUUID || isLoading) {
		return null;
	}

	return <ActiveRoadmapItemInternal collectionUUID={collectionUUID} />;
};

const emojiStyles = xcss({
	overflow: 'hidden',
	gridArea: 'icon',
	width: '24px',
	height: '22px',
	boxSizing: 'border-box',
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
	borderRadius: '3px 0 0 3px',
});
